import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ILead, LeadSource, LeadStatus } from "../../models/Lead";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import EnginesWorldwideDetails from "../vehicles/EnginesWorldwideDetails";
import VehicleDetails from "../vehicles/VehicleDetails";
import LeadIdentityImages from "./LeadInformation/LeadIdentityImages";
import LeadStatusCard from "./LeadInformation/LeadStatusCard";
import QuoteCalculationCard from "./LeadInformation/QuoteCalculationCard";
import WidgetQuoteDetails from "./LeadInformation/WidgetQuoteDetails";
import useLeadMutations from "../../data/useLeadMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import usePricingAccess from "../../hooks/usePricingAccess";
import useNotification from "../notifications/useNotifications";
import { useNavigate } from "react-router-dom";
import getAddress from "../../utils/getAddress";
import { getLeadLocation } from "../../utils/getLeadLocation";
import LeadDetailsTabs from "./LeadTabs/LeadDetailsTabs";
import LeadNotes from "./LeadInformation/LeadNotes";
import { useAuth } from "../../contexts/AuthContext";
import AuditLogDrawer from "../shared/AuditLogDrawer";
import VehicleNotes from "./LeadInformation/VehicleNotes";
import Modal from "../../layouts/Modal";
import BookingPricingCard from "./bookings/pricing/BookingPricingCard";
import ValuationScrapeCard from "../vehicles/ValuationScrapeCard";
import InitialPriceEstimateCard from "./InitialPriceEstimateCard";
import useCustomerDetails from "../../data/useCustomerDetails";
import { wilmaSAVCustomerId } from "../../config";
import { canAccessFeature } from "../../auth/featureScopes";


export default function LeadTab({ lead, setOpen, isBooking = false }: { lead: ILead, setOpen?: (open: boolean) => void, isBooking?: boolean }) {
  const { t } = useTranslation("leads");
  const navigate = useNavigate();

  const { claims, userId, customerId, featureScopes } = useAuth();
  const { canViewPrices } = usePricingAccess();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  // Presence-based like the sidebar; the card also asks the server, which fails closed.
  const { features } = useCustomerDetails();
  const hasInitialPriceEstimate = !!features.data?.data?.some((f) => f.name === "Initial Price Estimate");
  const isWidgetLead = lead?.website == LeadSource.SellAVehicle || lead?.website == LeadSource.Widget;
  // Same gate as the widget-dashboard routes, so the button never leads to a No Access page.
  const canOpenWidgetDashboard =
    isWidgetLead &&
    !!lead.id &&
    !!(claims?.isAdmin || claims?.isStaff || claims?.isMechanic) &&
    canAccessFeature(featureScopes, "widget");
  const { remove, update } = useLeadMutations(lead.id, "LeadTab");
  const { deleteHandlers } = useDefaultCRUDHandlers("Lead");
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { addNotification } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleDelete = () => {
    remove.mutate(lead.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        if (setOpen) {
          setOpen(false);
        } else {
          navigate("/leads");
        }
      },
      onError: (error) => {
        console.log(error);
        addNotification({
          variant: "error",
          primaryText: error,
        });
      },
    });
  };

  const handleOpenCancel = () => {
    setCancelReason((lead as any).cancelledReason ?? "");
    setCancelOpen(true);
  };

  const handleCancelLead = () => {
    const reason = cancelReason.trim();
    if (!reason) {
      addNotification({
        variant: "error",
        primaryText: t("Please provide a cancellation reason."),
      });
      return;
    }

    setCancelLoading(true);
    const updatedLead = {
      ...lead,
      status: LeadStatus.Cancelled,
      cancelledReason: reason,
      cancelledBy: userId ?? null,
      cancelledOn: new Date().toISOString(),
    } as ILead;

    update.mutate(
      updatedLead,
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setCancelOpen(false);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setCancelLoading(false);
        },
      }
    );
  };

  return (
    <>
      <ConfirmModal
        title={t("Delete Lead?")}
        message={t("Are you sure you want to delete this lead? This is a permanent action.")}
        confirmButtonText={null}
        onConfirm={handleDelete}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        isLoading={false}
      />
      <Modal open={cancelOpen} setOpen={setCancelOpen} width="max-w-2xl">
        <div className="space-y-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t("Cancel Lead")}</h3>
          <p className="text-sm text-gray-500">{t("Please enter a reason for cancelling this lead.")}</p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("Enter cancellation reason...")}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={() => setCancelOpen(false)}
              disabled={cancelLoading}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="rounded-md border border-transparent bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
              onClick={handleCancelLead}
              disabled={cancelLoading}
            >
              {cancelLoading ? t("Cancelling...") : t("Cancel Lead")}
            </button>
          </div>
        </div>
      </Modal>
      <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6">
        {/* Left hand thin column */}
        <div className="col-span-1 gap-6 flex flex-col">
          <div className="col-span-1 bg-white rounded-lg shadow">
            <iframe
              className="min-h-[50vh]"
              title="Google map"
              style={{ border: 0, borderRadius: "0.5rem" }}
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAJWEQovZtn6KkghJ5z6maiL0G6KHjwbS4&q=${getAddress(
                getLeadLocation(lead)
              )}&zoom=9`}
              width="100%"
              height="100%"
            ></iframe>
          </div>

          <VehicleDetails key={`vehicle-details-${lead?.vehicle?.id ?? lead?.id}`} vehicle={lead?.vehicle!} />
          {lead?.vehicle?.enginesWorldwide.map((offer) => (
            <EnginesWorldwideDetails key={offer.id} offer={offer} />
          ))}
          {isSavCustomer && lead?.vehicle?.id && (
            <ValuationScrapeCard key={`val-scrape-vehicle-${lead.vehicle.id}`} vehicleId={lead.vehicle.id} />
          )}

          {/* Calculation Breakdown — how we arrived at the purchase price, so it
              follows the pricing scope. */}
          {canViewPrices && (lead?.website == LeadSource.SellAVehicle || lead?.website == LeadSource.Widget) && (
            <>
              <QuoteCalculationCard key={`quote-calc-${lead.id}`} leadId={lead.id!} quoteType={lead.quoteType!} isCollection={lead.isCollection!} total={lead.price!} isBooking={isBooking} />
              <WidgetQuoteDetails key={`widget-quote-${lead.id}`} leadId={lead.id!} />
            </>
          )}

        </div>

        {/* Right hand wide column */}
        <div className="col-span-2 gap-6 flex flex-col">
          <LeadDetailsTabs lead={lead!} />
          <LeadStatusCard key={`lead-status-${lead.id}`} leadId={lead?.id!} />
          {/* Initial Price Estimate - the card hides itself unless the server says the customer has it. */}
          {canViewPrices && hasInitialPriceEstimate && lead?.id && (
            <InitialPriceEstimateCard key={`initial-price-estimate-${lead.id}`} leadId={lead.id} />
           )}
          <LeadIdentityImages key={`lead-images-${lead.id}`} lead={lead!} />
          {/* if bookings */}
          {isBooking && canViewPrices && (
            <BookingPricingCard key={`booking-pricing-${lead.id}`} lead={lead!} />
          )}
        </div>

        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 md:gap-x-4 gap-y-4">
          <LeadNotes key={`office-notes-${lead.id}`} lead={lead!} notesProp="officeNotes" title={t("Office Notes")} />
          <LeadNotes key={`driver-notes-${lead.id}`} lead={lead!} notesProp="driverNotes" title={t("Driver Notes")} />
          <LeadNotes key={`yard-notes-${lead.id}`} lead={lead!} notesProp="yardNotes" title={t("Yard Notes")} />
          <VehicleNotes key={`vehicle-notes-${lead?.vehicle?.id ?? lead.id}`} vehicle={lead.vehicle!} />
        </div>

        <div className="md:col-span-3 flex flex-wrap justify-end gap-2">
          {canOpenWidgetDashboard && (
            <button
              className="whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50"
              type="button"
              onClick={() => navigate(`/widget-dashboard/leads/${lead.id}`)}
            >
              {t("View on Widget Dashboard")}
            </button>
          )}
          {claims?.isAdmin && (
            <button
              className="whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50"
              type="button"
              onClick={() => setAuditOpen(true)}
            >
              {t("View Audit")}
            </button>
          )}

          <button
            className="whitespace-nowrap rounded-md border border-transparent bg-amber-600 px-3 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-amber-700"
            type="button"
            onClick={handleOpenCancel}
          >
            {t("Cancel Lead")}
          </button>
          <button
            className="whitespace-nowrap rounded-md border border-transparent bg-red-600 px-3 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-red-700"
            type="button"
            onClick={() => setDeleteOpen(true)}
          >
            {t("Delete Lead")}
          </button>
        </div>
      </div>

      {claims?.isAdmin && (
        <AuditLogDrawer
          open={auditOpen}
          setOpen={setAuditOpen}
          entityName="Lead"
          entityId={lead.id!}
        />
      )}
    </>
  )

}